/*
 *
 * AccountPage
 *
 */

import React from 'react';
import { connect } from 'react-redux';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { createStructuredSelector } from 'reselect';
import Wrapper from './Wrapper';
import FormTabs from 'components/FormTabs';
import AccountForm from './Forms/AccountForm'
import DeleteAccountForm from './Forms/DeleteAccountForm'
import BillingForm from './Forms/BillingForm'
import BoothCodeForm from './Forms/BoothCodeForm'
import { useFeatureFlag } from 'utils/posthog'
import { t } from 'copy/t'
import { selectUser, selectHasBillingPortal } from 'containers/App/selectors'
import { patchAccountRequest, deleteAccountRequest } from './actions'

export class AccountPage extends React.Component {
  // The selected tab is kept by name, not position: Billing and Booth code come
  // and go with server data and a PostHog flag, and an index would then point at
  // a different form (Delete account, at worst) mid-edit.
  state = { currentTabKey: 'BASIC_INFO', boothRecoveryCode: null };

  // The booth code's one-time recovery code lives here, not in the form: tabs
  // unmount their form on switch, and the code is shown only once.
  handleRecoveryCode = (boothRecoveryCode) => this.setState({ boothRecoveryCode });
  handleRecoveryDone = () => this.setState({ boothRecoveryCode: null });

  visibleTabKeys() {
    const { hasBillingPortal, boothCodeEnabled } = this.props;
    const keys = ['BASIC_INFO'];
    if (hasBillingPortal) keys.push('BILLING');
    // Hidden until the iPad app reads the account code (flag booth_code_enabled,
    // fail-closed OFF): a code set before then would not reach any booth.
    if (boothCodeEnabled) keys.push('BOOTH_CODE');
    keys.push('DELETE_ACCOUNT');
    return keys;
  }

  handleTabChange = (tab) => {
    this.setState({ currentTabKey: this.visibleTabKeys()[tab] });
  };

  render() {
    const { user, patchAccount, deleteAccount } = this.props;

    if(!user || user.email === '') {
      return <div></div>
    }

    const formsObj = {
      BASIC_INFO: {
        disabled: false,
        title: 'ACCOUNT',
        renderForm: () =>
          <AccountForm
            initialValues={user}
            handleSubmit={patchAccount}
          />
      },
      BILLING: {
        disabled: false,
        title: 'BILLING',
        renderForm: () => <BillingForm />
      },
      BOOTH_CODE: {
        disabled: false,
        title: t('boothCode.tabTitle'),
        renderForm: () =>
          <BoothCodeForm
            recoveryCode={this.state.boothRecoveryCode}
            onRecoveryCode={this.handleRecoveryCode}
            onRecoveryDone={this.handleRecoveryDone}
          />
      },
      DELETE_ACCOUNT: {
        disabled: false,
        title: 'DELETE ACCOUNT',
        renderForm: () =>
          <DeleteAccountForm
            handleSubmit={deleteAccount}
          />
      }
    }

    // Billing sits between the account details and the destructive tab, which
    // is both where a customer would look for it and what keeps DELETE ACCOUNT
    // last. Omitted entirely for an account the server has told us has no
    // Stripe customer behind it — a tab that could only ever error is worse
    // than no tab (see selectHasBillingPortal for why an ABSENT field still
    // shows it).
    const keys = this.visibleTabKeys()
    const tabs = keys.map((key) => formsObj[key])
    // A selected tab that has just disappeared falls back to Account, never to
    // whatever now sits at its old position.
    const currentTab = Math.max(keys.indexOf(this.state.currentTabKey), 0)

    return (
      <Wrapper>
        <HelmetProvider>
          <Helmet
            title="AccountPage"
            meta={[
              { name: 'description', content: 'Description of AccountPage' },
            ]}
          />
        </HelmetProvider>
        {
          user
            ?
            <FormTabs
              formTitle="My Account"
              currentTab={currentTab}
              onTabChange={this.handleTabChange}
              tabs={tabs}
            />
            :
            undefined
        }
      </Wrapper>
    );
  }
}

const mapStateToProps = (state) => createStructuredSelector({
  user: selectUser(),
  hasBillingPortal: selectHasBillingPortal(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  patchAccount: (data) => {
    dispatch(patchAccountRequest(data))
  },
  deleteAccount: (data) => {
    dispatch(deleteAccountRequest(data))
  },
});

export const BOOTH_CODE_FLAG = 'booth_code_enabled';

// AccountPage is a class component; the flag hook needs a function wrapper.
const AccountPageWithFlags = (props) => (
  <AccountPage {...props} boothCodeEnabled={useFeatureFlag(BOOTH_CODE_FLAG)} />
);

export default connect(mapStateToProps, mapDispatchToProps)(AccountPageWithFlags);
